import * as React from 'react';
import { Indexable } from 'src/types/Primitive';
import type { AppState } from 'src/reducers';
import { WrappedDispatch, wrappedDispatch } from 'src/utils/Redux/Dispatch';
import { identity } from 'src/utils/Primitive/Function';
import { connect } from 'react-redux';
import { DataGrid } from 'src/common-ui/index';
import { SelectionOverride } from 'src/common-ui/components/Filters/Filters';
import { DataGridProps } from 'src/common-ui/components/DataGrid/DataGrid';
import Renderer from 'src/utils/Domain/Renderer';
import getWeekRangeText from 'src/utils/Functions/Description';
import GridErrorBoundary from 'src/components/ErrorBoundary/GridErrorBoundary';
import { IS_HINDSIGHTING } from 'src/utils/Domain/ConstantsFunctions';
import { FilterModelType } from 'src/components/Sidenav/SideNav.slice';
import { getSectionContext } from 'src/utils/Domain/Perspective';
import { ColumnState } from '@ag-grid-community/core';
import { ViewDataState } from 'src/types/Domain';
import { parseCompanionListViewConfig } from 'src/utils/Component/ListView';
import { getCompanionDataIndex } from 'src/components/ListGridPair/ListGridPair.utils';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { OptionGroup } from 'src/components/Configure/ConfigureModal';
import { getConfigureSelections } from 'src/components/Subheader/Subheader.selectors';
import { getSelectedFloorset } from 'src/components/ConfigurableGrid/ConfigurableGrid.selectors';

type FilterSensitiveProps = any;
type InternalFilterSensitiveProps = {
  customHeader: string;
  excelTitle: string;
  excelScope: string;
  excelFilters: string;
  excelExportSource: string;
  excelViewDetails: string[];
  parentProps: Indexable;
};

type ExcelViewDetailsOwnProps = {
  /** Label for the Floorset/Superset dropdown, or omit if this view has none. */
  floorsetLabel?: string;
  /** Whether this view has a Group By dropdown at all. */
  hasGroupBy?: boolean;
  /**
   * Display name of the selected companion-list item, for grids (e.g. `ListGridPair`/`GridView`) whose
   * selection lives in local component state rather than redux
   */
  selectedCompanionItem?: string;
  configureOptionGroups?: OptionGroup[];
  /**
   * Needed so it doesnt leak into export for views that dont have it configured
   */
  showFlowStatus?: boolean;
};

interface FilterSensitiveState {
  gridFilterModel: FilterModelType;
  gridSortModel: ColumnState[];
}

export function getHeaderTextFromState(state: AppState, skipFilters = false): string {
  const filterSelections: SelectionOverride[] = state.filters.selectionOverrides;

  const selectedFilters = filterSelections
    .filter((filterSelection) => filterSelection.value) // only include "enabled" overrides; the overrides may contain values that aren't saved but have been turned off by the user
    .map((filterSelection) => filterSelection.id)
    .toString();
  const filterText = skipFilters ? '' : `Filters: ${selectedFilters} \r\n`;

  const view = state.perspective.activePage;
  const currentContext = getSectionContext();

  const { productMemberName, locationMemberName, historyStart, historyEnd, start, end } = state.scope.scope;
  const { pastRangeList, rangeList } = state.scope;

  const currentScope = IS_HINDSIGHTING(currentContext)
    ? getWeekRangeText(historyStart, historyEnd, pastRangeList)
    : getWeekRangeText(start, end, rangeList);

  return `${productMemberName} | Location - ${locationMemberName} | ${currentScope} \r\n${filterText}Exported from: ${view}`;
}
/** New helper to get & Store export data in separate values rather than 1 line format in `getHeaderTextFromState`
 * and also includes view title to display at the top of the export.
 * Left the existing function as its used in multiple places
 */
function getExcelExportMetaFromState(state: AppState, skipFilters = false) {
  const filterSelections: SelectionOverride[] = state.filters.selectionOverrides;

  const selectedFilters = filterSelections
    .filter((filterSelection) => filterSelection.value)
    .map((filterSelection) => filterSelection.id)
    .toString();

  const filterText = skipFilters || !selectedFilters ? '' : selectedFilters;
  const viewTitle = state.perspective.activePageConf?.name;
  const view = state.perspective.activePage;
  const currentContext = getSectionContext();

  const { productMemberName, locationMemberName, historyStart, historyEnd, start, end } = state.scope.scope;
  const { pastRangeList, rangeList } = state.scope;

  const currentScope = IS_HINDSIGHTING(currentContext)
    ? getWeekRangeText(historyStart, historyEnd, pastRangeList)
    : getWeekRangeText(start, end, rangeList);

  return {
    excelTitle: viewTitle ? `${viewTitle} - Export` : 'S5 Excel Export',
    excelScope: `${productMemberName} | Location - ${locationMemberName} | ${currentScope}`,
    excelFilters: filterText,
    excelExportSource: view,
  };
}

//#region excelViewDetails
/**
 * Gathering `exportOptions.excelViewDetails` (the "View Details" row on Excel exports)
 *
 * Search/Flow Status/Worklist selection are read straight from `state` below.
 * Configure selections are also read from state, but check prop `configureOptionGroups`
 * to confirm configure prescence in view. Floorset/GroupBy work the same way,
 * gated by `floorsetLabel`/`hasGroupBy`. Companion-item selection is
 * the one true exception: it lives in parents own local component state,
 * so it must be passed in.
 */

/** Worklist Companion's selected level & selected item, when applicable */
function getWorklistExcelViewDetails(state: AppState): string[] {
  const { worklist } = state;
  const details: string[] = [];

  const levelLabel = worklist.selectedLevel?.text ?? worklist.selectedLevel?.dataIndex;
  if (levelLabel) {
    details.push(`Level: ${levelLabel}`);
  }

  const itemMappings = worklist.viewDefn?.companionView?.itemMappings;
  if (worklist.selectedItemId && itemMappings) {
    const lookup = parseCompanionListViewConfig(itemMappings as unknown as TenantConfigViewData);
    const titleKey = getCompanionDataIndex(lookup.titleId); // matches selectedItemId to a row, not the display text
    const bodyKey = getCompanionDataIndex(lookup.bodyId); // the rows display name
    const flat =
      worklist.viewDataState === ViewDataState.liveDataReady ? worklist.liveData.flat : worklist.cachedData.flat;
    const item = titleKey ? (flat as Indexable[]).find((row) => row[titleKey] === worklist.selectedItemId) : undefined;
    const itemLabel = item && bodyKey && item[bodyKey];
    if (itemLabel) {
      details.push(`${levelLabel ?? 'Selected Item'}: ${itemLabel}`);
    }
  }

  return details;
}

/**
 * Floorset/Superset selection, read via `getSelectedFloorset` — `floorsetLabel` confirms
 * the current view actually has this dropdown.
 */
function getFloorsetExcelViewDetail(state: AppState, label: string | undefined): string[] {
  if (!label) {
    return [];
  }
  const { topMemberOptions } = state.subheader;
  const selected = topMemberOptions?.find((option) => option.id === getSelectedFloorset(state));
  return selected ? [`${label}: ${selected.name}`] : [];
}

/**
 * Selected Flow Status and Funded Status filters, when applicable
 */
function getFlowStatusExcelViewDetails(state: AppState): string[] {
  const { flowStatus, flowStatusConfig } = state.subheader;
  const flowStatusOptions = flowStatusConfig?.values?.length
    ? flowStatusConfig
    : state.appConfig.tenantConfig.flowStatus;
  if (!flowStatusOptions?.values?.length) {
    return [];
  }
  const fundedStatusOptions = state.appConfig.tenantConfig.fundedStatus;
  const selections = flowStatus ?? [];

  const flowLabels = selections
    .filter((index) => index < 10)
    .map((index) => flowStatusOptions.values[index]?.text)
    .filter((text): text is string => typeof text === 'string');
  const details = [`Flow Status: ${flowLabels.length ? flowLabels.join(', ') : 'All'}`];

  const fundedLabels = selections
    .filter((index) => index >= 10)
    .map((index) => fundedStatusOptions?.values?.[index - 10]?.text)
    .filter((text): text is string => typeof text === 'string');
  if (fundedLabels.length) {
    details.push(`Funded Status: ${fundedLabels.join(', ')}`);
  }

  return details;
}

/** Grid search box value, when applicable */
function getSearchExcelViewDetails(state: AppState): string[] {
  const { search } = state.subheader;
  return search ? [`Search: "${search}"`] : [];
}

/**
 * Group By selection, read straight from `state.subheader.groupBy`
 * — `hasGroupBy` confirms the current view has the dropdown.
 */
function getGroupByExcelViewDetail(state: AppState, hasGroupBy: boolean | undefined): string[] {
  if (!hasGroupBy) {
    return [];
  }
  const { groupBy } = state.subheader;
  const option = groupBy?.options?.[groupBy.selection ?? -1];
  return option?.text ? [`Group By: ${option.text}`] : [];
}

/**
 * Configure modal's selected levels, as "Configure: Department - Class - Style". `configureSelections`
 * only shows up once the user picks something outside the view defaults. It's
 * read straight from state here since `configureOptionGroups` confirms the view has Configure.
 */
function getConfigureExcelViewDetails(state: AppState, optionGroups: OptionGroup[] | undefined): string[] {
  const selections = getConfigureSelections(state);
  if (!optionGroups?.length || !selections?.length) {
    return [];
  }
  const configureLevels = selections
    .map((selection) => selection?.text)
    .filter((text): text is string => typeof text === 'string');
  return configureLevels.length ? [`Configure: ${configureLevels.join(' - ')}`] : [];
}

export function getExcelViewDetailsFromState(state: AppState, ownProps: ExcelViewDetailsOwnProps): string[] {
  return [
    ...getWorklistExcelViewDetails(state),
    ...getFloorsetExcelViewDetail(state, ownProps.floorsetLabel),
    ...getGroupByExcelViewDetail(state, ownProps.hasGroupBy),
    ...((ownProps.showFlowStatus ?? true) ? getFlowStatusExcelViewDetails(state) : []),
    ...getSearchExcelViewDetails(state),
    ...(ownProps.selectedCompanionItem ? [`Selected Item: ${ownProps.selectedCompanionItem}`] : []),
    ...getConfigureExcelViewDetails(state, ownProps.configureOptionGroups),
  ];
}
//#endregion excelViewDetails

export function makeDataGridFilterSensitive<T extends React.ComponentClass>(WrappedComponent: T) {
  function mergeProps(
    state: AppState,
    _wrapped: WrappedDispatch,
    parentProps: DataGridProps & ExcelViewDetailsOwnProps,
  ) {
    const customHeader = getHeaderTextFromState(state);
    const { excelTitle, excelScope, excelFilters, excelExportSource } = getExcelExportMetaFromState(state);
    const excelViewDetails = getExcelViewDetailsFromState(state, parentProps);

    const props: InternalFilterSensitiveProps = {
      customHeader,
      excelTitle,
      excelScope,
      excelFilters,
      excelExportSource,
      excelViewDetails,
      parentProps,
    };
    const activeFavorite = state.subheader.favoritesList?.find((fav) => fav.active);
    const favoriteFilterModel: FilterModelType | undefined = activeFavorite?.jsonBlob?.gridFilterModel;
    const favoriteSortModel: ColumnState[] | undefined = activeFavorite?.jsonBlob?.gridSortModel;
    const itemsStatus = {
      planning: state.planTracker.planItems,
      worklist: state.pages.hindsighting.styleColorReview.sharedData.worklist,
    };
    return {
      ...props,
      parentProps: {
        ...parentProps,
        favoriteFilterModel,
        favoriteSortModel,
        itemsStatus,
      },
    };
  }

  class DataGridFilterSensitive extends React.Component<FilterSensitiveProps, FilterSensitiveState> {
    constructor(props: FilterSensitiveProps) {
      super(props);

      this.state = {
        gridFilterModel: {},
        gridSortModel: [],
      };
    }

    storeGridFilterModel = (gridFilterModel: FilterModelType) => {
      this.setState({
        gridFilterModel,
      });
    };

    storeGridSortModel = (gridSortModel: ColumnState[]) => {
      this.setState({
        gridSortModel,
      });
    };

    render() {
      const { gridFilterModel, gridSortModel } = this.state;
      const additionalExcelExports = {
        customHeader: this.props.customHeader, // keep for backward compatibility if needed
        excelTitle: this.props.excelTitle,
        excelScope: this.props.excelScope,
        excelFilters: this.props.excelFilters,
        excelExportSource: this.props.excelExportSource,
        excelViewDetails: this.props.excelViewDetails,
        excelRendererObj: Renderer.getAllExcelRenderers(),
      };

      const resolvedFilterModel = this.props.parentProps.favoriteFilterModel || gridFilterModel;
      const resolvedSortModel = this.props.parentProps.favoriteSortModel || gridSortModel;
      const finalParentProps = {
        ...this.props.parentProps,
        gridFilterModel: resolvedFilterModel,
        gridSortModel: resolvedSortModel,
        storeGridFilterModel: this.storeGridFilterModel,
        storeGridSortModel: this.storeGridSortModel,
        itemsStatus: this.props.parentProps.itemsStatus,
      };
      return (
        <React.Fragment>
          <GridErrorBoundary errorTitle={'Something has gone wrong with the grid'}>
            <WrappedComponent
              {...finalParentProps}
              exportOptions={{
                ...additionalExcelExports,
                ...finalParentProps.exportOptions,
              }}
            />
          </GridErrorBoundary>
        </React.Fragment>
      );
    }
  }
  // @ts-ignore
  return connect(identity, wrappedDispatch, mergeProps)(DataGridFilterSensitive);
}

// @ts-ignore
export default makeDataGridFilterSensitive(DataGrid);
